/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
  Edit as EditIcon,
  Search as SearchIcon,
} from 'react-feather';
import { useSnackbar } from 'notistack';
import Request from '../../../requests/api/companies';
import { SUPER_ADMIN } from '../../../utils/const/user_types';

const sortOptions = [
  {
    value: 'updated_at|desc',
    label: 'Más nuevo primero',
  },
  {
    value: 'updated_at|asc',
    label: 'Más antiguo primero',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {},
  queryField: {
    width: 500,
  },
  bulkOperations: {
    position: 'relative',
  },
  bulkActions: {
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: 6,
    position: 'absolute',
    width: '100%',
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
  },
  bulkAction: {
    marginLeft: theme.spacing(2),
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
}));

function Results({
  className,
  companies,
  pageOfTable,
  setPageOfTable,
  limit,
  setLimit,
  searchValue,
  setSearchValue,
  totalCompanies,
  ...rest
}) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [sort, setSort] = useState(sortOptions[0].value);
  const [currentCompanies, setCurrentCompanies] = useState(companies);

  useEffect(() => { setCurrentCompanies(companies); }, [companies]);

  const handleQueryChange = (event) => {
    event.persist();
    setSearchValue(event.target.value);
  };

  const handleSortChange = (event) => {
    event.persist();
    setSort(event.target.value);
  };

  const handleSelectAllCompanies = (event) => {
    setSelectedCompanies(event.target.checked
      ? currentCompanies.map((company) => company.id)
      : []);
  };

  const handleSelectOneCompany = (event, companyId) => {
    if (!selectedCompanies.includes(companyId)) {
      setSelectedCompanies((prevSelected) => [...prevSelected, companyId]);
    } else {
      setSelectedCompanies((prevSelected) => prevSelected.filter((id) => id !== companyId));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPageOfTable(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  // Usually query is done on backend with indexing solutions
  const enableBulkOperations = selectedCompanies.length > 0;
  const selectedSomeCompanies = selectedCompanies.length > 0 && selectedCompanies.length < currentCompanies.length;
  const selectedAllCompanies = selectedCompanies.length === currentCompanies.length;
  const account = useSelector((state) => state.account);

  const deleteCompanies = async () => {
    if (account.user.company.name !== SUPER_ADMIN) {
      enqueueSnackbar('Debe ser Administrador!', {
        variant: 'error',
      });
    } else if (selectedCompanies.includes(account.user.id)) {
      enqueueSnackbar('No puede eliminar su propia cuenta', {
        variant: 'error',
      });
    } else {
      for (let a = 0; a < selectedCompanies.length; a += 1) {
        Request.deleteCompany(selectedCompanies[a]);
        enqueueSnackbar('Cuentas eliminadas!', {
          variant: 'success',
        });
      }
      setCurrentCompanies(currentCompanies.filter((e) => (!selectedCompanies.includes(e.id))));
      setSelectedCompanies([]);
    }
  };

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Divider />
      <Box
        p={2}
        minHeight={56}
        display="flex"
        alignItems="center"
      >
        <TextField
          className={classes.queryField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon
                  fontSize="small"
                  color="action"
                >
                  <SearchIcon />
                </SvgIcon>
              </InputAdornment>
            ),
          }}
          onChange={handleQueryChange}
          placeholder="Buscar usuario"
          value={searchValue}
          variant="outlined"
        />
        <Box flexGrow={1} />
        <TextField
          label="Orden"
          name="sort"
          onChange={handleSortChange}
          select
          SelectProps={{ native: true }}
          value={sort}
          variant="outlined"
        >
          {sortOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </TextField>
      </Box>
      {enableBulkOperations && (
        <div className={classes.bulkOperations}>
          <div className={classes.bulkActions}>
            <Checkbox
              checked={selectedAllCompanies}
              indeterminate={selectedSomeCompanies}
              onChange={handleSelectAllCompanies}
            />
            <Button
              variant="outlined"
              className={classes.bulkAction}
              onClick={() => deleteCompanies()}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAllCompanies}
                    indeterminate={selectedSomeCompanies}
                    onChange={handleSelectAllCompanies}
                  />
                </TableCell>
                <TableCell>
                  Nombre
                </TableCell>
                <TableCell align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentCompanies.map((company) => {
                const isCompanySelected = selectedCompanies.includes(company.id);

                return (
                  <TableRow
                    hover
                    key={company.id}
                    selected={isCompanySelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isCompanySelected}
                        onChange={(event) => handleSelectOneCompany(event, company.id)}
                        value={isCompanySelected}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                          <Typography
                            color="inherit"
                            variant="h6"
                          >
                            {company.name}
                          </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        component={RouterLink}
                        to={`/administracion/companias/${company.id}/editar`}
                      >
                        <SvgIcon fontSize="small">
                          <EditIcon />
                        </SvgIcon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={totalCompanies}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={pageOfTable}
        rowsPerPage={limit}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página"
      />
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  companies: PropTypes.array,
  pageOfTable: PropTypes.number,
  setPageOfTable: PropTypes.func,
  limit: PropTypes.number,
  setLimit: PropTypes.func,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  totalCompanies: PropTypes.number,
};

Results.defaultProps = {
  companies: [],
};

export default Results;
